// ============================================================================
// L'ÉTAT D'UN CLIENT — le signal le plus grave de son parc : la colonne « État » de la liste et les pastilles de l'en-tête
// de sa fiche (maquette du 28/10).
//
// UTILITÉ : dire d'un coup d'œil quel client traiter d'abord. CALCULÉ, JAMAIS ÉCRIT : les blocages viennent des blocages
// eux-mêmes, « hors ligne » et « silencieux » du calcul de la Supervision (`storeHealth`), la mise en service de l'annuaire
// des magasins — la liste ne peut donc contredire ni la fiche ni la Supervision.
// ⚠️ « Période fiscale en attente » n'y est pas : aucune source ne la remonte à la Console (SA-9.2). On ne l'invente pas.
// ⚠️ « À mettre en service » et « sans magasin » y sont (décision du 28/10) : le compteur « Clients » du menu compte les
//    magasins à mettre en service, la colonne doit montrer ce qu'il compte.
// ============================================================================
import { codeCaisse } from '@orgazone/ui-web';
import type { TenantBlock } from './api';
import { storeHealth, type HealthInput } from './sync-health';
import type { Tone } from './ui';

export interface ClientSignal {
  /** Gravité : 0 = la plus grave. */
  sev: number;
  label: string;
  tone: Tone;
  /** Où (« R01 · Sélestat », un magasin, « abonnement ») — `null` quand le signal vaut pour tout le client. */
  where: string | null;
}

/** Un magasin de l'annuaire : son nom et son statut (`provisioning` = à mettre en service). */
interface DirectoryStore { name: string; status: string }

/** Les signaux d'un blocage, du plus grave au moins grave. */
function blockSignal(b: TenantBlock): ClientSignal {
  const lock = b.level === 'lock';
  if (b.scope === 'subscription') return { sev: lock ? 0 : 2, label: lock ? 'client bloqué' : 'client averti', tone: lock ? 'da' : 'wa', where: 'abonnement' };
  if (b.scope === 'store') return { sev: lock ? 1 : 2, label: lock ? 'magasin bloqué' : 'magasin averti', tone: lock ? 'da' : 'wa', where: b.storeName };
  // Une caisse porte un numéro ; un autre appareil (Mobile Partner) son code d'équipement.
  const till = b.registerNumber != null;
  const code = till ? codeCaisse(String(b.registerNumber)) : b.equipmentCode;
  const where = [code, b.storeName].filter(Boolean).join(' · ') || null;
  if (till) return { sev: lock ? 1 : 2, label: lock ? 'caisse bloquée' : 'caisse avertie', tone: lock ? 'da' : 'wa', where };
  return { sev: lock ? 1 : 2, label: lock ? 'appareil bloqué' : 'appareil averti', tone: lock ? 'da' : 'wa', where };
}

/**
 * Les signaux d'un client, du plus grave au moins grave.
 * `health` : la Supervision des magasins du client — `null` quand elle n'a pas pu être lue (on ne juge alors ni le hors
 * ligne ni le silence, et l'écran le dit).
 */
export function clientSignals(input: {
  blocks: readonly TenantBlock[];
  stores: readonly DirectoryStore[];
  health: readonly (HealthInput & { name: string })[] | null;
}, now = Date.now()): ClientSignal[] {
  const out: ClientSignal[] = input.blocks.map(blockSignal);
  for (const s of input.health ?? []) {
    const h = storeHealth(s, now);
    if (s.status && s.status !== 'active') continue;
    const where = h.worst ? `${h.worst.code} · ${s.name}` : s.name;
    if (h.kind === 'da') {
      out.push({ sev: 3, tone: 'da', where, label: !h.worst ? 'base injoignable' : h.worst.till ? 'caisse hors ligne' : 'terminal hors ligne' });
    } else if (h.kind === 'wa') {
      out.push({ sev: 4, tone: 'wa', where, label: h.worst?.till === false ? 'terminal silencieux' : 'caisse silencieuse' });
    }
  }
  if (input.stores.length === 0) out.push({ sev: 5, label: 'sans magasin', tone: 'wa', where: null });
  for (const s of input.stores) if (s.status === 'provisioning') out.push({ sev: 5, label: 'à mettre en service', tone: 'wa', where: s.name });
  return out.sort((a, b) => a.sev - b.sev);
}

/**
 * La pastille de mise en service de l'en-tête d'une fiche client — `null` quand tous ses magasins sont en service.
 * ⚠️ Elle reste, quand la maquette retire « actif » : un client dont aucun magasin n'est en service se traite autrement
 *    (décision du 28/10), et « actif » affirmait le contraire (retour utilisateur du 2026-08-11).
 */
export function provisioningPill(stores: readonly DirectoryStore[]): { label: string; tone: Tone } | null {
  if (stores.length === 0) return { label: 'sans magasin', tone: 'wa' };
  const waiting = stores.filter((s) => s.status === 'provisioning').length;
  if (waiting === 0) return null;
  if (waiting === stores.length) return { label: stores.length > 1 ? 'aucun magasin en service' : 'à mettre en service', tone: 'wa' };
  return { label: `${waiting} à mettre en service`, tone: 'wa' };
}

/**
 * La pastille de blocage de l'en-tête d'une fiche client — `null` sans blocage.
 * « bloqué » quand un blocage arrête tout le client (son abonnement) ; sinon le nombre de blocages en cours.
 */
export function blockPill(blocks: readonly Pick<TenantBlock, 'scope' | 'level'>[]): { label: string; tone: Tone } | null {
  if (blocks.length === 0) return null;
  if (blocks.some((b) => b.scope === 'subscription' && b.level === 'lock')) return { label: 'bloqué', tone: 'da' };
  return { label: `${blocks.length} blocage${blocks.length > 1 ? 's' : ''} en cours`, tone: 'wa' };
}
